import { getConfigs } from "../configs/Config";
import { fetchIdToken, fetchRefreshToken, decodeToken } from "../utils/Auth";

// Whitelist of authorized user emails for risk score approval
// These are the email addresses for Keli Munk, Morgan McDorman, and Nagasai Kotha
const AUTHORIZED_USER_EMAILS = [
  "keli_munk@avalonbay.com",
  "morgan_mcdorman@avalonbay.com",
  "nagasai_kotha.c@avalonbay.com",
];

/**
 * Check if current user is authorized to approve applications with risk scores
 * @returns {boolean} - True if user is authorized, false otherwise
 */
const isAuthorizedForRiskScoreApproval = (): boolean => {
  try {
    const token = fetchIdToken();
    if (!token) return false;

    // Remove "Bearer " prefix if present
    const cleanToken = token.replace(/^Bearer\s+/, "");
    const decodedToken = decodeToken(cleanToken);

    const userId = decodedToken["cognito:username"] || decodedToken.name;

    return (
      userId &&
      AUTHORIZED_USER_EMAILS.some((authorizedId) =>
        userId.toLowerCase().includes(authorizedId.toLowerCase())
      )
    );
  } catch (error) {
    console.error("Error checking approveWithRiskScore authorization:", error);
    return false;
  }
};

const getToken = async (user) => {
  const { apiKey, baseRoute } = getConfigs();

  const requestOptions = {
    method: "POST",
    headers: new Headers({
      "content-type": "application/json",
      "x-api-key": apiKey || "",
    }),
    body: JSON.stringify({
      emailAddress: user.email,
      password: user.password,
    }),
  };

  const url = `${baseRoute}/authenticate`;
  const resp = await fetch(url, requestOptions);
  return resp;
};

const getTokensFromAuthorizationCode = async (code) => {
  const { apiKey, baseRoute } = getConfigs();

  const requestOptions = {
    method: "POST",
    headers: new Headers({
      "content-type": "application/json",
      "x-api-key": apiKey || "",
    }),
    body: JSON.stringify({
      code: code,
    }),
  };

  const url = `${baseRoute}/credentials`;
  const resp = await fetch(url, requestOptions);
  return resp;
};

const refreshToken = async () => {
  const { apiKey, baseRoute } = getConfigs();

  const requestOptions = {
    method: "POST",
    headers: new Headers({
      "content-type": "application/json",
      "x-api-key": apiKey || "",
      Authorization: fetchIdToken() || "",
    }),
    body: JSON.stringify({
      refreshToken: fetchRefreshToken(),
    }),
  };

  const url = `${baseRoute}/refresh-token`;
  const resp = await fetch(url, requestOptions);
  return resp;
};

const AuthService = {
  getToken,
  getTokensFromAuthorizationCode,
  refreshToken,
  isAuthorizedForRiskScoreApproval,
};

export default AuthService;
